import { IUser, IUpdateUser } from '../../protocolos';
import { Environment } from '../../environments';
import { Api } from '../api/axios-config';

export type TUserDeletedNeedsReactivation = {
    code: 'USER_DELETED_NEEDS_REACTIVATION' | 'ACCOUNT_DEACTIVATED';
    message: string;
    cpf?: string;
    name?: string;
    email?: string;
};

export type TCreateUserResult = IUser | TUserDeletedNeedsReactivation | Error;

const isUserDeletedNeedsReactivation = (data: any): data is TUserDeletedNeedsReactivation => {
    return data?.code === 'USER_DELETED_NEEDS_REACTIVATION' ||
        data?.code === 'ACCOUNT_DEACTIVATED';
};

const getErrorMessage = (error: unknown, fallback: string): string => {
    const errorData = error as any;

    return (
        errorData?.response?.data?.message ||
        errorData?.message ||
        fallback
    );
};

type TUserDataCount = {
    data: IUser[];
    totalCount: number;
}

const listAll = async (sort: string, page: number, order: string, like?: string): Promise<TUserDataCount | Error> => {
    try {
        const url = `/user?limit=${Environment.LIMIT}&skip=${Environment.SKIP}&sort=${sort}&like=${like ? like : ""}&page=${page}&order=${order}&includeInactive=true`;
        const { data } = await Api.get(url, {});

        if (data) {
            return {
                data: data.data,
                totalCount: data.count
            };
        }

        return new Error('Erro ao listar os registros.');
    } catch (error) {
        console.error(error);
        return new Error(getErrorMessage(error, 'Erro ao listar os registros.'));
    }
};

const listAllUserWhenIsAdminAndNotEstablishment = async (sort: string, page: number, order: string, like?: string): Promise<TUserDataCount | Error> => {
    try {
        const url = `/user/admin?limit=${Environment.LIMIT}&skip=${Environment.SKIP}&sort=${sort}&like=${like ? like : ""}&page=${page}&order=${order}`;
        const { data } = await Api.get(url, {});

        if (data) {
            return {
                data: data.data,
                totalCount: data.count
            };
        }

        return new Error('Erro ao listar os registros.');
    } catch (error) {
        console.error(error);
        return new Error(getErrorMessage(error, 'Erro ao listar os registros.'));
    }
};

const getById = async (id: string): Promise<IUser | Error> => {
    try {
        const { data } = await Api.get(`/user/${id}`, {});

        if (data) {
            return data;
        }

        return new Error('Erro ao consultar o registro.');
    } catch (error) {
        console.error(error);
        return new Error(getErrorMessage(error, 'Erro ao consultar o registro.'));
    }
};

const getByIdForReactivate = async (id: string): Promise<IUser | Error> => {
    try {
        const { data } = await Api.get(`/user/reactivate/${id}`, {});

        if (data) {
            return data;
        }

        return new Error('Erro ao consultar o usuário excluído.');
    } catch (error) {
        console.error(error);
        return new Error(getErrorMessage(error, 'Erro ao consultar o usuário excluído.'));
    }
};

const listAllClientCompanyUsers = async (
    filter = '',
    page = 1,
    limit = 10,
    sort = 'name',
    order: 'asc' | 'desc' = 'asc'
): Promise<TUserDataCount | Error> => {
    try {
        const url = `/user/client-company?like=${filter}&page=${page}&limit=${limit}&sort=${sort}&order=${order}`;

        const { data } = await Api.get(url);

        if (data) {
            return {
                data: data.data,
                totalCount: data.count,
            };
        }

        return new Error('Erro ao listar usuários da empresa cliente.');
    } catch (error) {
        console.error(error);
        return new Error(getErrorMessage(error, 'Erro ao listar usuários da empresa cliente.'));
    }
};

const create = async (dados: Omit<IUser, 'id'>): Promise<TCreateUserResult> => {
    try {
        const { data } = await Api.post<{ user: IUser }>('/user/admin', dados, {});

        if (data?.user) {
            return data.user;
        }

        return new Error('Erro ao criar o registro.');
    } catch (error) {
        console.error(error);

        const errorData = (error as any)?.response?.data;

        if (isUserDeletedNeedsReactivation(errorData)) {
            return errorData;
        }

        return new Error(getErrorMessage(error, 'Erro ao criar o registro.'));
    }
};

const update = async (id: string, dados: IUpdateUser): Promise<void | Error> => {
    try {
        await Api.put(`/user/admin/${id}`, dados, {});
    } catch (error) {
        console.error(error);
        return new Error(getErrorMessage(error, 'Erro ao atualizar o registro.'));
    }
};

const reactivateByCpf = async (
    cpf: string,
    reactivatedBy: string
): Promise<void | Error> => {
    try {
        await Api.put('/user/reactivate-by-cpf', {
            cpf,
            reactivatedBy,
        }, {});
    } catch (error) {
        console.error(error);
        return new Error(getErrorMessage(error, 'Erro ao reativar o usuário.'));
    }
};

const deleteById = async (id: string): Promise<void | Error> => {
    try {
        await Api.delete(`/user/admin/${id}/PERMANENT`, {});
    } catch (error) {
        console.error(error);
        return new Error(getErrorMessage(error, 'Erro ao apagar o registro.'));
    }
};

const disableById = async (id: string): Promise<void | Error> => {
    try {
        await Api.delete(`/user/admin/${id}/TEMPORARY`, {});
    } catch (error) {
        console.error(error);
        return new Error(getErrorMessage(error, 'Erro ao desabilitar o usuário.'));
    }
};

const reactivateById = async (id: string): Promise<void | Error> => {
    try {
        await Api.patch(`/user/admin/${id}/reactivate`, {}, {});
    } catch (error) {
        console.error(error);
        return new Error(getErrorMessage(error, 'Erro ao reativar o usuário.'));
    }
};

const resendConfirmationEmailByAdmin = async (userId: string): Promise<{ message: string } | Error> => {
    try {
        const { data } = await Api.post(`/user/admin/${userId}/resend-confirmation-email`);
        return data;
    } catch (error) {
        return new Error(getErrorMessage(error, 'Nao foi possivel reenviar a confirmacao.'));
    }
};

export const UserService = {
    listAll,
    listAllUserWhenIsAdminAndNotEstablishment,
    listAllClientCompanyUsers,
    deleteById,
    getById,
    update,
    create,
    reactivateByCpf,
    reactivateById,
    disableById,
    resendConfirmationEmailByAdmin,
};
